<div class="bw-dialog detail-dialog">

    <lib-page-header [title]="mode === 'create' ? 'Add User Data' : 'Edit User Data'" backTo="">
        <mat-icon icon>{{ mode === 'create' ? 'add_circle' : 'edit' }}</mat-icon>
        <span subtitle>{{ mode === 'create'
                ? 'Provision a new row.'
                : 'Update an existing UserData row.' }}</span>
    </lib-page-header>

    <div class="bw-body">
        <form [formGroup]="form" class="ud-form">

            <div class="form-section app-panel">
                <div class="section-title">Identity</div>
                <div class="ud-row two-col">
                    <mat-form-field appearance="outline">
                        <mat-label>Type</mat-label>
                        <mat-icon matPrefix class="field-icon">category</mat-icon>
                        <input matInput formControlName="type"
                               placeholder="e.g. NE / PROBE / CIRCUIT"
                               [matAutocomplete]="typeAuto" />
                        <mat-autocomplete #typeAuto="matAutocomplete">
                            <mat-option *ngFor="let t of filteredTypes" [value]="t">{{ t }}</mat-option>
                        </mat-autocomplete>
                        <mat-hint *ngIf="mode === 'edit'">Type is fixed once a row exists</mat-hint>
                        <mat-error *ngIf="form.controls['type'].hasError('required')">Type is required</mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Key</mat-label>
                        <mat-icon matPrefix class="field-icon">vpn_key</mat-icon>
                        <input matInput formControlName="key"
                               placeholder="e.g. NE-NYC-CORE-01" />
                        <mat-hint *ngIf="mode === 'edit'">Key is fixed once a row exists</mat-hint>
                        <mat-error *ngIf="form.controls['key'].hasError('required')">Key is required</mat-error>
                    </mat-form-field>
                </div>

                <mat-form-field appearance="outline" class="ud-full">
                    <mat-label>Label</mat-label>
                    <mat-icon matPrefix class="field-icon">label</mat-icon>
                    <input matInput formControlName="label"
                           placeholder="Friendly name shown in lists" />
                </mat-form-field>

                <div class="ud-row toggle-row">
                    <mat-slide-toggle formControlName="isResource" color="primary">
                        Resource
                    </mat-slide-toggle>
                    <span class="toggle-hint">
                        Toggle on when a runner must hold this row exclusively for the
                        duration of an execution (e.g. "configure THIS NE"). Reveals
                        the capacity / cost / availability card below.
                    </span>
                </div>
            </div>

            <!-- Resource card (P3.2). Only meaningful when isResource is on;
                 hidden otherwise. Type-level defaults from typeOptions() are
                 surfaced inline as placeholders so the operator sees what
                 they're overriding. -->
            <div class="form-section app-panel resource-card"
                 *ngIf="form.controls['isResource'].value">
                <div class="section-title">
                    <mat-icon>inventory_2</mat-icon> Resource card
                    <span class="section-hint">
                        Per-row overrides for capacity, reusability, availability and
                        cost. Leave a field blank to inherit the type-level default.
                    </span>
                </div>

                <div class="ud-row three-col">
                    <mat-form-field appearance="outline">
                        <mat-label>Capacity</mat-label>
                        <mat-icon matPrefix class="field-icon">inventory_2</mat-icon>
                        <input matInput type="number" min="0" step="1"
                               formControlName="capacity"
                               placeholder="∞" />
                        <span matTextSuffix *ngIf="typeOptions()?.resourceCapacityUnit as unit">
                            &nbsp;{{ unit }}
                        </span>
                        <mat-hint *ngIf="!typeOptions()?.resourceCapacityUnit">
                            Max simultaneous claims. Blank = unbounded.
                        </mat-hint>
                        <mat-hint *ngIf="typeOptions()?.resourceCapacityUnit as unit">
                            Unit: {{ unit }} (from type default)
                        </mat-hint>
                        <mat-error *ngIf="form.controls['capacity'].hasError('min')">
                            Capacity cannot be negative
                        </mat-error>
                    </mat-form-field>

                    <div class="reusable-toggle-cell">
                        <mat-slide-toggle formControlName="reusable" color="primary">
                            Reusable (pool)
                        </mat-slide-toggle>
                        <span class="toggle-sub">
                            <ng-container *ngIf="form.controls['reusable'].value === true">
                                Pool — capacity replenishes on release.
                            </ng-container>
                            <ng-container *ngIf="form.controls['reusable'].value === false">
                                Burn-down — each claim consumes a slot.
                            </ng-container>
                            <ng-container *ngIf="form.controls['reusable'].value === null">
                                <em>Inheriting type default
                                    ({{ typeOptions()?.reusableDefault === false
                                          ? 'burn-down' : 'pool' }}).</em>
                            </ng-container>
                        </span>
                    </div>

                    <mat-form-field appearance="outline"
                                    *ngIf="form.controls['reusable'].value === false">
                        <mat-label>Capacity remaining</mat-label>
                        <mat-icon matPrefix class="field-icon">inventory</mat-icon>
                        <input matInput type="number" min="0" step="1"
                               formControlName="capacityRemaining" />
                        <mat-hint>Burn-down counter. Usually reset via admin action.</mat-hint>
                        <mat-error *ngIf="form.controls['capacityRemaining'].hasError('min')">
                            Cannot be negative
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="ud-row two-col">
                    <mat-form-field appearance="outline">
                        <mat-label>Cost per use</mat-label>
                        <mat-icon matPrefix class="field-icon">payments</mat-icon>
                        <input matInput type="number" min="0" step="0.01"
                               formControlName="costPerUse"
                               [placeholder]="(typeOptions()?.costPerUseDefault | json) ?? '0.00'" />
                        <mat-hint *ngIf="typeOptions()?.costPerUseDefault !== null
                                       && typeOptions()?.costPerUseDefault !== undefined">
                            Type default: {{ typeOptions()?.costPerUseDefault }}
                            {{ typeOptions()?.costCurrencyDefault }}
                        </mat-hint>
                        <mat-error *ngIf="form.controls['costPerUse'].hasError('min')">
                            Cost cannot be negative
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Currency</mat-label>
                        <mat-icon matPrefix class="field-icon">payments</mat-icon>
                        <input matInput type="text" maxlength="3"
                               formControlName="costCurrency"
                               [placeholder]="typeOptions()?.costCurrencyDefault ?? 'USD'"
                               style="text-transform: uppercase" />
                        <mat-hint>ISO 4217 (e.g. USD, EUR, CAD).</mat-hint>
                        <mat-error *ngIf="form.controls['costCurrency'].hasError('maxlength')">
                            3-letter code only
                        </mat-error>
                    </mat-form-field>
                </div>

                <!-- Availability JSON. Collapsed by default — most rows inherit
                     the always-available type default. Expand to author a
                     timezone+days+windows policy (raw JSON for v1; richer
                     editor in a follow-up). -->
                <mat-expansion-panel class="availability-panel" [expanded]="false">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="panel-icon">schedule</mat-icon>
                            Availability policy
                        </mat-panel-title>
                        <mat-panel-description>
                            <ng-container *ngIf="!form.controls['availability'].value
                                              && !typeOptions()?.availabilityDefault">
                                Always available (no per-row override).
                            </ng-container>
                            <ng-container *ngIf="!form.controls['availability'].value
                                              && typeOptions()?.availabilityDefault">
                                Inheriting type-level policy.
                            </ng-container>
                            <ng-container *ngIf="form.controls['availability'].value">
                                Per-row override set.
                            </ng-container>
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <mat-form-field appearance="outline" class="ud-full availability-field">
                        <mat-label>Availability JSON</mat-label>
                        <mat-icon matPrefix class="field-icon">schedule</mat-icon>
                        <textarea matInput rows="6" wrap="off"
                                  formControlName="availability"
                                  spellcheck="false"
                                  autocomplete="off"
                                  placeholder='{ "timezone": "America/Toronto", "days": [1,2,3,4,5], "windows": [{"start":"09:00","end":"17:00"}] }'></textarea>
                        <mat-hint>
                            Empty = inherit type default / always available. Shape:
                            <code>timezone</code>, <code>days</code> (1=Mon … 7=Sun),
                            <code>windows</code> (24h <code>HH:mm</code>).
                        </mat-hint>
                    </mat-form-field>
                </mat-expansion-panel>
            </div>

            <div class="form-section app-panel">
                <div class="section-title">
                    <mat-icon>groups</mat-icon> Org scoping
                    <span class="section-hint">All three optional. Empty = not scoped at that level (visible to everyone).</span>
                </div>
                <div class="ud-row three-col">
                    <mat-form-field appearance="outline">
                        <mat-label>Financial unit</mat-label>
                        <mat-icon matPrefix class="field-icon">account_balance</mat-icon>
                        <input matInput formControlName="financialUnit"
                               placeholder="e.g. BU-SAAS"
                               [matAutocomplete]="financialUnitAuto" />
                        <mat-autocomplete #financialUnitAuto="matAutocomplete">
                            <mat-option *ngFor="let f of filteredFinancialUnits" [value]="f.code">
                                <span class="ud-opt-code">{{ f.code }}</span>
                                <span class="ud-opt-sep">—</span>
                                <span class="ud-opt-name">{{ f.name }}</span>
                            </mat-option>
                        </mat-autocomplete>
                        <mat-hint>Top level (P&amp;L)</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Org unit</mat-label>
                        <mat-icon matPrefix class="field-icon">business</mat-icon>
                        <input matInput formControlName="orgUnit"
                               placeholder="e.g. NA-CA"
                               [matAutocomplete]="orgUnitAuto" />
                        <mat-autocomplete #orgUnitAuto="matAutocomplete">
                            <mat-option *ngFor="let o of filteredOrgUnits" [value]="o.code">
                                <span class="ud-opt-code">{{ o.code }}</span>
                                <span class="ud-opt-sep">—</span>
                                <span class="ud-opt-name">{{ o.name }}</span>
                            </mat-option>
                        </mat-autocomplete>
                        <mat-hint>Mid level (operational)</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Department</mat-label>
                        <mat-icon matPrefix class="field-icon">groups</mat-icon>
                        <input matInput formControlName="department"
                               placeholder="e.g. ENG-FE"
                               [matAutocomplete]="departmentAuto" />
                        <mat-autocomplete #departmentAuto="matAutocomplete">
                            <mat-option *ngFor="let d of filteredDepartments" [value]="d.code">
                                <span class="ud-opt-code">{{ d.code }}</span>
                                <span class="ud-opt-sep">—</span>
                                <span class="ud-opt-name">{{ d.name }}</span>
                            </mat-option>
                        </mat-autocomplete>
                        <mat-hint>Finest level (team)</mat-hint>
                    </mat-form-field>
                </div>
            </div>

            <!-- Schema-driven editor (P3.1). Renders one form-field per declared
                 attribute on the selected type. Hidden when the type has zero
                 attributes (e.g. brand-new type), in which case the raw JSON
                 section below is the only payload editor. Edits propagate
                 into the `data` JSON via deep-merge — declared paths overwrite,
                 non-declared payload keys are preserved. -->
            <div class="form-section app-panel" *ngIf="schemaForType() as sch">
                <div class="section-title">
                    <mat-icon>schema</mat-icon> Schema fields
                    <span class="section-hint">
                        Declared attributes for <code>{{ form.controls['type'].value }}</code>.
                        Edits here merge into the JSON payload below.
                    </span>
                </div>
                <app-schema-form
                        [schema]="sch"
                        [value]="schemaValue()"
                        (valueChange)="onSchemaFormChange($event)">
                </app-schema-form>
            </div>

            <!-- Payload section. Mirrors the audit-trail detail dialog
                 chrome (snapshot-section / snapshot-body / snapshot-content).
                 Default mode is the read-only ngx-json-viewer; clicking the
                 pencil icon toggles to a textarea for editing. -->
            <div class="form-section app-panel payload-section">
                <div class="snapshot-body">
                    <div class="snapshot-label">
                        <mat-icon class="snap-icon">data_object</mat-icon>
                        Payload (JSON)
                        <span class="snap-spacer"></span>
                        <button type="button" mat-icon-button class="payload-btn"
                                *ngIf="editingJson()"
                                (click)="togglePretty()"
                                [disabled]="jsonError() !== null"
                                [matTooltip]="pretty()
                                        ? 'Compact: collapse to a single line'
                                        : 'Pretty: re-indent on multiple lines'"
                                [attr.aria-label]="pretty() ? 'Compact JSON to a single line' : 'Pretty-print JSON on multiple lines'">
                            <mat-icon>{{ pretty() ? 'compress' : 'expand' }}</mat-icon>
                        </button>
                        <button type="button" mat-icon-button class="payload-btn"
                                *ngIf="editingJson()"
                                (click)="formatJson()"
                                [disabled]="jsonError() !== null"
                                matTooltip="Reformat as 2-space indented JSON"
                                aria-label="Reformat as 2-space indented JSON">
                            <mat-icon>format_indent_increase</mat-icon>
                        </button>
                        <button type="button" mat-icon-button class="payload-btn"
                                (click)="toggleEditMode()"
                                [disabled]="editingJson() && jsonError() !== null"
                                [matTooltip]="editingJson()
                                        ? (jsonError() !== null
                                                ? 'Fix JSON to return to viewer'
                                                : 'Done — return to viewer')
                                        : 'Edit JSON'"
                                [attr.aria-label]="editingJson() ? 'Return to JSON viewer' : 'Edit JSON'">
                            <mat-icon>{{ editingJson() ? 'check' : 'edit' }}</mat-icon>
                        </button>
                    </div>
                    <div class="snapshot-content" [class.has-error]="editingJson() && jsonError() !== null">
                        <ng-container *ngIf="!editingJson(); else editPayload">
                            <app-json-path-viewer *ngIf="parsedJson() !== null; else noPayload"
                                                   [value]="parsedJson()"></app-json-path-viewer>
                            <ng-template #noPayload>
                                <div class="empty">No JSON payload — click the edit icon to add one.</div>
                            </ng-template>
                        </ng-container>
                        <ng-template #editPayload>
                            <textarea class="json-input" formControlName="data"
                                      spellcheck="false"
                                      autocomplete="off"
                                      autocapitalize="off"
                                      autocorrect="off"
                                      wrap="off"
                                      placeholder='{ "vendor": "Cisco", "model": "ASR9904" }'></textarea>
                        </ng-template>
                    </div>
                </div>
                <div class="payload-foot">
                    <span class="payload-hint" *ngIf="!editingJson()">
                        Stored verbatim. Click the edit icon to modify.
                    </span>
                    <span class="payload-hint" *ngIf="editingJson() && jsonError() === null">
                        Edit the JSON, then click ✓ to return to the viewer.
                    </span>
                    <span class="payload-error" *ngIf="editingJson() && jsonError()">
                        <mat-icon>error</mat-icon>
                        {{ jsonError() }}
                    </span>
                </div>
            </div>

        </form>
    </div>

    <mat-dialog-actions class="dialog-actions" align="end">
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-flat-button color="primary"
                [disabled]="!canSave()"
                (click)="onSave()">
            <mat-icon>{{ mode === 'create' ? 'add' : 'save' }}</mat-icon>
            {{ mode === 'create' ? 'Create' : 'Save' }}
        </button>
    </mat-dialog-actions>

</div>
